package app.footyguide.web.page

import app.footyguide.core.theme.ClubCatalog
import app.footyguide.domain.model.BroadcastStatus
import app.footyguide.domain.model.Game
import app.footyguide.domain.model.MatchStatus
import app.footyguide.web.data.WebChannelRepository
import app.footyguide.web.data.WebCompetitionRepository
import app.footyguide.web.data.WebTeamRepository

private val LIVE_STATUSES = setOf(
    MatchStatus.FIRST_HALF, MatchStatus.HALFTIME,
    MatchStatus.SECOND_HALF, MatchStatus.EXTRA_TIME, MatchStatus.PENALTIES,
)

private const val TV_ICON_SVG = """<svg class="fg-icon-tv" xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m17 2-5 5-5-5"/><rect width="20" height="15" x="2" y="7" rx="2"/></svg>"""

/**
 * Game card redesign — "onde assistir" como hero.
 *
 * Layout:
 *   Horário (esq)                  Competição (dir)
 *   🔴 FLA · Flamengo                            2
 *   🟢 PAL · Palmeiras                           1
 *   📺 Premiere · SporTV · CazéTV
 */
fun renderGameCardHtml(
    game: Game,
    teams: Map<String, WebTeamRepository.TeamData> = emptyMap(),
    competitions: Map<String, WebCompetitionRepository.CompetitionData> = emptyMap(),
    channels: Map<String, WebChannelRepository.ChannelData> = emptyMap(),
    inGroup: Boolean = false,
): String {
    val slug = buildGameSlug(game)
    val targetRoute = buildGameRoute(game)
    val homeTeam = teams[game.homeTeamId]
    val awayTeam = teams[game.awayTeamId]
    val competition = competitions[game.competitionId]

    val homeName = displayName(game.homeTeamId, homeTeam?.name)
    val awayName = displayName(game.awayTeamId, awayTeam?.name)
    val homeClub = ClubCatalog.bySlug(game.homeTeamId)
    val awayClub = ClubCatalog.bySlug(game.awayTeamId)
    val homeInitials = homeClub?.initials ?: homeName.take(3).uppercase()
    val awayInitials = awayClub?.initials ?: awayName.take(3).uppercase()

    val compName = competitionDisplayName(
        id = game.competitionId,
        shortName = competition?.shortName,
        resolvedName = competition?.name,
    )

    val time = extractKickoffTime(game.kickoff)
    val homeLogoHtml = renderLogoHtml(
        homeTeam?.logo,
        homeName,
        "fg-game-card-team-logo",
        "fg-game-card-team-fallback",
    )
    val awayLogoHtml = renderLogoHtml(
        awayTeam?.logo,
        awayName,
        "fg-game-card-team-logo",
        "fg-game-card-team-fallback",
    )

    val isLive = game.matchStatus in LIVE_STATUSES
    val isFinished = game.matchStatus == MatchStatus.FINISHED

    // Top row: live badge or time (left) + competition (right)
    val contextHtml = renderMatchContextHtml(game)
    val topLeft = if (isLive) {
        """<div class="fg-card-meta"><span class="fg-live-badge">AO VIVO</span>$contextHtml</div>"""
    } else {
        """<div class="fg-card-meta"><time datetime="${game.kickoff}" itemprop="startDate" class="fg-card-time${if (isFinished) " fg-card-time-finished" else ""}">$time</time>$contextHtml</div>"""
    }

    val topRight = if (!inGroup) {
        val compLogoHtml = renderLogoHtml(competition?.logo, compName, "fg-competition-logo")
        """<span class="fg-card-comp">$compLogoHtml $compName</span>"""
    } else ""

    // Score
    val homeScore = game.homeScore
    val awayScore = game.awayScore
    val hasScore = homeScore != null && awayScore != null
    val scoreClass = when {
        isLive -> "fg-score fg-score-live"
        isFinished -> "fg-score fg-score-finished"
        else -> "fg-score"
    }
    val homeScoreHtml = if (hasScore) """<span class="$scoreClass">$homeScore</span>""" else ""
    val awayScoreHtml = if (hasScore) """<span class="$scoreClass">$awayScore</span>""" else ""

    // Channels — THE HERO (Visual-First & Unslop)
    val channelIds = orderChannelsForDisplay(game.channelsFor("BR"))
    val channelHtml = if (channelIds.isNotEmpty()) {
        val badges = channelIds.joinToString("") { ch ->
            val chData = channels[ch]
            val chName = displayName(ch, chData?.name)
            """<span class="fg-ch">$chName</span>"""
        }
        """<div class="fg-card-channels"><span class="fg-card-channels-icon fg-icon-tv-svg">$TV_ICON_SVG</span>$badges</div>"""
    } else {
        val broadcastLabel = when (game.broadcastStatusFor("BR")) {
            BroadcastStatus.PENDING -> """<div class="fg-card-channels fg-card-channels-pending"><span class="fg-card-channels-icon fg-icon-tv-svg">$TV_ICON_SVG</span><span class="fg-ch">A confirmar</span></div>"""
            BroadcastStatus.UNAVAILABLE -> ""
            else -> ""
        }
        broadcastLabel
    }

    return """
        <article class="fg-game-card" itemscope itemtype="https://schema.org/SportsEvent">
            <a href="$targetRoute" onclick="event.preventDefault(); footyguide.navigateTo('$targetRoute')"
               title="Onde assistir $homeName x $awayName" aria-label="$homeName x $awayName — $time">
                <div class="fg-card-top">$topLeft$topRight</div>
                <div class="fg-card-matchup">
                    ${renderGameTeamRowHtml("home", homeLogoHtml, homeName, homeInitials, homeScoreHtml)}
                    ${renderGameTeamRowHtml("away", awayLogoHtml, awayName, awayInitials, awayScoreHtml)}
                </div>
                $channelHtml
            </a>
        </article>
    """.trimIndent()
}

fun renderSkeletonCards(count: Int = 3): String {
    return (1..count).joinToString("") {
        """<div class="fg-skeleton fg-skeleton-card" aria-hidden="true"></div>"""
    }
}

private fun renderGameTeamRowHtml(
    side: String,
    logoHtml: String,
    name: String,
    initials: String,
    scoreHtml: String,
): String {
    return """
        <div class="fg-game-card-team fg-game-card-team--$side" itemprop="${side}Team" itemscope itemtype="https://schema.org/SportsTeam">
            <span class="fg-game-card-team-logo-slot">$logoHtml</span>
            <span class="fg-game-card-team-name" itemprop="name">
                <span class="fg-team-identity">
                    <strong class="fg-team-initials">$initials</strong>
                    <span class="fg-game-card-team-name-text">$name</span>
                </span>
            </span>
            $scoreHtml
        </div>
    """.trimIndent()
}

private const val BASE62 = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz"

fun ensureAlphanumericMatchId(rawId: String): String {
    val trimmed = rawId.trim()
    if (trimmed.isEmpty()) return ""
    if (trimmed.length == 8 && trimmed.all { it in '0'..'9' || it in 'A'..'Z' || it in 'a'..'z' }) {
        return trimmed
    }
    val normalized = trimmed.lowercase()
    var h1 = 0x811c9dc5u
    var h2 = 0x9e3779b9u
    for (ch in normalized) {
        val code = ch.code.toUInt()
        h1 = (h1 xor code) * 0x01000193u
        h2 = (h2 xor (code + 0x7fu)) * 0x85ebca6bu
    }
    return buildString(8) {
        var v1 = h1
        var v2 = h2
        for (i in 0 until 4) {
            append(BASE62[(v1 % 62u).toInt()])
            v1 /= 62u
        }
        for (i in 0 until 4) {
            append(BASE62[(v2 % 62u).toInt()])
            v2 /= 62u
        }
    }
}

fun buildGameSlug(game: Game): String = "${game.homeTeamId}-${game.awayTeamId}"

fun buildGameRoute(game: Game, locale: String = "pt"): String {
    val cleanSlug = buildGameSlug(game)
    val shortId = ensureAlphanumericMatchId(game.id)
    val idPart = if (shortId.isNotBlank()) "/$shortId" else ""
    return when (locale) {
        "en" -> "/en/match/$cleanSlug$idPart"
        "es" -> "/es/partido/$cleanSlug$idPart"
        else -> "/jogo/$cleanSlug$idPart"
    }
}

fun formatDateLabel(kickoff: String): String {
    val dateParts = extractIsoDate(kickoff).split("-")
    return "${dateParts[2]}/${dateParts[1]}"
}

fun isGameLive(game: Game): Boolean = game.matchStatus in LIVE_STATUSES

fun hasConfirmedBroadcast(game: Game): Boolean =
    game.broadcastStatusFor("BR") == BroadcastStatus.CONFIRMED

fun renderMatchContextHtml(game: Game): String {
    val labels = mutableListOf<String>()

    // Ordem de prioridade: Grupo -> Fase -> Rodada
    // Se tem grupo (ex: Grupo A), mostramos o grupo.
    // Se tem fase (ex: Semifinal), mostramos a fase.
    // Se tem rodada (ex: 3ª Rodada), mostramos a rodada.

    game.group?.let { labels.add("Grupo $it") }
    game.stage?.let { labels.add(it) }
    game.matchday?.let { labels.add("${it}ª Rodada") }

    if (labels.isEmpty()) return ""

    // Mostra apenas o mais relevante ou combina conforme necessário. 
    // Por enquanto, vamos pegar o primeiro disponível da nossa lista de prioridade.
    val mainLabel = labels.first()
    return """<span class="fg-card-context"> · $mainLabel</span>"""
}

fun renderStadiumLabel(game: Game): String {
    val stadium = game.stadium ?: return ""
    val city = game.stadiumCity
    val label = if (city != null) "$stadium, $city" else stadium
    return """<span class="fg-stadium" itemprop="location" itemscope itemtype="https://schema.org/Place"><span itemprop="name">$label</span></span>"""
}
